import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CartItem from './CartItem';
import '@fontsource/ibm-plex-mono';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { closeShoppingCart, openLogin } from '../redux/slices/modalSlice';
import { useNavigate } from 'react-router-dom';
import { setOrderItems } from '../redux/slices/orderSlice';
import { removeFromCart } from '../redux/slices/cartSlice';
import { processBooksData } from "../services/booksService";

const Container = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafa 100%);
  border-radius: 24px;
  padding: 32px;
  max-width: 720px;
  width: auto;
  margin: 0 auto;
  font-family: 'IBM Plex Mono', monospace;
  box-shadow: 0 8px 32px rgba(59, 98, 85, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 90vh;
  min-width: 350px;
  
  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
    border-radius: 16px;
    max-height: 95vh;
  }
`;

const Title = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #3B6255;
  margin: 0 0 32px 0;
  text-align: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 24px;
  }
`;

const ItemsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(59, 98, 85, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #3B6255 0%, #2d4a3d 100%);
    border-radius: 3px;
    
    &:hover {
      background: linear-gradient(180deg, #2d4a3d 0%, #1a2f23 100%);
    }
  }
  
  scrollbar-width: thin;
  scrollbar-color: #3B6255 rgba(59, 98, 85, 0.1);
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  background: linear-gradient(135deg, #3B6255 0%, #2d4a3d 100%);
  padding: 20px 24px;
  border-radius: 16px;
  color: white;
  font-family: 'IBM Plex Mono', monospace;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
  width: 100%;
  box-shadow: 0 4px 12px rgba(59, 98, 85, 0.3);
  
  @media (max-width: 768px) {
    padding: 16px 20px;
    font-size: 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const ContinueButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 700;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 16px;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
  font-family: 'IBM Plex Mono', monospace;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #3B6255;
  font-size: 18px;
  font-family: 'IBM Plex Mono', monospace;
`;

const StockNotice = styled.div`
  max-width: 550px;
  color: #ae0404;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'IBM Plex Mono', monospace;
`;

interface CartBook {
  item_id: number;
  book_id: number;
  book_title: string;
  author_name: string;
  book_price: number;
  book_cover_path: string;
  book_quantity: number;
  stock: number; 
}

const ShoppingCart: React.FC = () => {
  const [addedToCart, setAddedToCart] = useState(false);
  const customerId = useSelector((state: RootState) => state.auth.customer_id);
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAuthChecked = useSelector((state: RootState) => state.auth.isAuthChecked);
  const dispatch = useDispatch();
  const [items, setItems] = useState<CartBook[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const cart = useSelector((state: RootState) => state.cart);

  const fetchCart = async () => {
  if (!customerId || !token) return;
    try {
      setLoading(true);
      const booksData: CartBook[] = await processBooksData(cart.items);
      setItems(booksData);
    } catch (error) {
      console.error('Помилка завантаження кошика:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId || !token) return;
    fetchCart();
  }, [customerId, token]);

  const handleQuantityChange = async (itemId: number, delta: number) => {
    const item = items.find(i => i.item_id === itemId);
    if (!item) return;

    const newQuantity = item.book_quantity + delta;

    if (delta < 0 && newQuantity < 1) {
      return;
    }

    if (delta > 0 && newQuantity > item.stock) {
      alert('Недостатньо книг на складі');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}/shopping-cart/item/${itemId}?customerId=${customerId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            book_quantity: newQuantity,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Помилка оновлення кількості: ${response.status}`);
      }

      setItems(prevItems => 
        prevItems.map(i => 
          i.item_id === itemId 
            ? { ...i, book_quantity: newQuantity }
            : i
        )
      );
    } catch (error) {
      console.error('Не вдалося оновити кількість:', error);
    }
  };

  const handleDelete = async (itemId: number, bookId: number) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API_URL}/shopping-cart/item/${itemId}?customerId=${customerId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Помилка видалення товару: ${response.status}`);
      }

      setItems(prevItems => prevItems.filter(item => item.item_id !== itemId));
      dispatch(removeFromCart(bookId));
      setAddedToCart(false);
      console.log('Товар видалено з кошика');
    } catch (error) {
      console.error('Не вдалося видалити товар:', error);
    }
  };

  const handleContinue = () => {
    const formattedItems = items.map(item => {
      return ({
      id: String(item.book_id),
      name: item.book_title,
      price: item.book_price,
      quantity: item.book_quantity,
      author: item.author_name,
      cover: item.book_cover_path,
    })});
    dispatch(setOrderItems(formattedItems));
    dispatch(closeShoppingCart());
    navigate("/order");
  };

  if (isAuthChecked && !customerId) {
    dispatch(closeShoppingCart());
    dispatch(openLogin());
    return null;
  }

  if (loading) {
    return (
      <Container>
        <LoadingState>Завантаження кошика...</LoadingState>
      </Container>
    );
  }

  const total = items.reduce((sum, item) => sum + item.book_price * item.book_quantity, 0).toFixed(2);

  const hasStockIssues = items.some(item => item.book_quantity > item.stock || item.stock === 0);

  return (
    <Container>
      <Title>Кошик</Title>
      
      {hasStockIssues && (
        <StockNotice style={{ textAlign: 'center', fontWeight: 700, marginBottom: '16px' }}>
          Деяких книг немає в наявності в достатній кількості, будь ласка змініть кількість товару в кошику щоб перейти до оформлення замовлення
        </StockNotice>
      )}
      
      {items.length === 0 ? (
        <EmptyState>Ваш кошик порожній</EmptyState>
      ) : (
        <>
          <ItemsWrapper>
            {items.map(item => (
              <CartItem
                key={item.item_id}
                image={item.book_cover_path}
                title={item.book_title}
                author={item.author_name}
                price={item.book_price}
                quantity={item.book_quantity}
                stock={item.stock}
                onIncrement={() => handleQuantityChange(item.item_id, 1)}
                onDecrement={() => handleQuantityChange(item.item_id, -1)}
                onDelete={() => handleDelete(item.item_id, item.book_id)}
              >
                {item.stock === 0 && <StockNotice>Даний товар закінчився</StockNotice>}
                {item.stock > 0 && item.book_quantity > item.stock && (
                  <StockNotice>На складі залишилось лише {item.stock} шт.</StockNotice>
                )}
              </CartItem>
            ))}
          </ItemsWrapper>
          <Footer>
            <div>Разом: {total} грн</div>
            <ContinueButton
              onClick={handleContinue}
              disabled={hasStockIssues}
              style={{ opacity: hasStockIssues ? 0.5 : 1, cursor: hasStockIssues ? 'not-allowed' : 'pointer' }}
            >
              Продовжити ➝
            </ContinueButton>
          </Footer>
        </>
      )}
    </Container>
  );
};

export default ShoppingCart;